import type { FullTranslation } from "../../../../../lib/i18n"

import { DISQUALIFY_T } from "@/lib/apps/autoleads/disqualify"
import { LEAD_CATEGORY_T } from "@/lib/apps/autoleads/lead-category"
import { STEP_LABEL_T } from "@/lib/apps/autoleads/step-labels"

export const TASK_BOARD_T = {
  // Shared with the sequence editor: STEP_META.labelKey resolves here too.
  ...STEP_LABEL_T,
  // Shared with the exit-reason pie: DISQUALIFY_REASON_LABEL resolves here.
  ...DISQUALIFY_T,
  // Shared with the global CRM: the four lead categories.
  ...LEAD_CATEGORY_T,
  // ── columns ──
  columnWaiting: { FR: "En attente", EN: "Waiting" },
  columnTerminal: { FR: "Terminé", EN: "Done" },
  columnEmpty: { FR: "Aucun contact ici.", EN: "No contact here." },
  loadMore: { FR: "Charger plus", EN: "Load more" },

  // ── toolbar ──
  searchPlaceholder: {
    FR: "Rechercher une entreprise...",
    EN: "Search a company...",
  },
  onlyActionable: { FR: "À faire uniquement", EN: "To do only" },
  viewSwitchLabel: { FR: "Affichage", EN: "View" },
  boardEmpty: {
    FR: "Aucun contact dans la séquence pour le moment.",
    EN: "No contact in the sequence yet.",
  },
  scrollBoardLeft: {
    FR: "Voir les colonnes précédentes",
    EN: "Show previous columns",
  },
  scrollBoardRight: {
    FR: "Voir les colonnes suivantes",
    EN: "Show next columns",
  },
  boardEmptyHint: {
    FR: "Les contacts apparaissent ici dès la première synchronisation de la campagne.",
    EN: "Contacts appear here as soon as the campaign runs its first sync.",
  },

  // ── card ──
  // The column is already called "En attente", so the card only owes the name.
  waitingBehind: {
    FR: "derrière {name}",
    EN: "behind {name}",
  },
  disqualify: { FR: "Écarter", EN: "Rule out" },
  cardMoved: {
    FR: "Contact replacé sur cette étape.",
    EN: "Contact moved to this step.",
  },
  queuePosition: { FR: "{n}e de la file", EN: "#{n} in queue" },
  stepsProgress: { FR: "{done}/{total} étapes", EN: "{done}/{total} steps" },
  openProfile: { FR: "Profil", EN: "Profile" },
  callContact: { FR: "Appeler", EN: "Call" },
  copyBody: { FR: "Copier le message", EN: "Copy message" },
  copyEmail: { FR: "Copier l'email", EN: "Copy email" },
  copied: { FR: "Copié", EN: "Copied" },
  copyEmpty: {
    FR: "Aucun texte rédigé pour cette étape.",
    EN: "No copy written for this step.",
  },
  copyFailed: { FR: "Copie impossible.", EN: "Could not copy." },
  markDone: { FR: "Fait", EN: "Done" },
  dropFile: { FR: "Déposer le fichier", EN: "Drop the file" },
  dropFileHint: {
    FR: "Déposez ou choisissez le fichier de ce contact : l'email suivant y renverra par un lien.",
    EN: "Drop or pick this contact's file: the next email will link to it.",
  },
  fileUploading: { FR: "Envoi…", EN: "Uploading…" },
  fileDropped: { FR: "Fichier ajouté", EN: "File added" },
  copyDocument: { FR: "Document lié :", EN: "Linked document:" },
  skipStep: { FR: "Passer cette étape", EN: "Skip this step" },
  reopenStep: { FR: "Rouvrir", EN: "Reopen" },
  history: { FR: "Historique", EN: "History" },
  // The header's reachability icons: which channels this contact can be worked
  // on, before the rep opens anything.
  reachPhone: { FR: "Téléphone : {value}", EN: "Phone: {value}" },
  reachMail: {
    FR: "Séquence rédigée pour {value}",
    EN: "Sequence written for {value}",
  },
  reachLinkedin: { FR: "Profil LinkedIn connu", EN: "LinkedIn profile known" },
  noLinkedinUrl: {
    FR: "Aucun profil LinkedIn connu : les étapes LinkedIn sont passées.",
    EN: "No known LinkedIn profile: the LinkedIn steps are skipped.",
  },

  // ── task kinds ──
  automaticSending: { FR: "Envoi automatique", EN: "Sent automatically" },
  // Who executes the current step: the single most useful thing a card can say
  // before the rep decides whether to touch it.
  kindAutomatic: { FR: "Automatique", EN: "Automatic" },
  kindManual: { FR: "À faire vous-même", EN: "You do this" },
  // Only ever shown once the tool actually holds the contact.
  automaticBy: { FR: "Envoi par {tool}", EN: "Sending via {tool}" },
  viewCopy: { FR: "Voir le message", EN: "View message" },
  copyNotDrafted: {
    FR: "Ce message n'a pas été rédigé. Oups, c'est de notre faute.",
    EN: "This message was never drafted. Oops, that one is on us.",
  },
  copyNotDraftedShort: {
    FR: "Message non rédigé",
    EN: "Message not drafted",
  },
  redraftCopy: { FR: "Rédiger le message", EN: "Draft the message" },
  scriptNotDrafted: {
    FR: "Le script de cet appel n'a pas encore été rédigé pour ce contact.",
    EN: "This call's script has not been drafted for this contact yet.",
  },
  scriptNotDraftedShort: {
    FR: "Script non rédigé",
    EN: "Script not drafted",
  },
  redraftScript: { FR: "Rédiger le script", EN: "Draft the script" },
  redraftRunning: {
    FR: "Rédaction en cours, environ une minute.",
    EN: "Drafting, about a minute.",
  },
  copySubject: { FR: "Objet", EN: "Subject" },
  copyBodyLabel: { FR: "Message", EN: "Message" },
  // A call step's drafted copy: the script the rep reads, and what to press on.
  viewScript: { FR: "Voir le script", EN: "View script" },
  copyPainPoints: {
    FR: "Points de douleur à creuser",
    EN: "Pain points to press on",
  },
  copyTo: { FR: "Destinataire : {to}", EN: "To: {to}" },
  automaticHint: {
    FR: "Votre outil d'envoi s'en occupe, rien à faire.",
    EN: "Your sending tool handles this, nothing to do.",
  },
  dueOn: { FR: "Prévu le {date}", EN: "Due {date}" },
  overdue: { FR: "En retard", EN: "Overdue" },
  overdueBy: { FR: "En retard de {n} j", EN: "{n} d overdue" },
  // Terse on purpose: the answers plus their icons have to fit a 288px column,
  // and the column header already asks "Invitation acceptée ?". ``checkRefused``
  // is no longer an answer, but older check logs still carry it.
  checkAccepted: { FR: "Oui", EN: "Yes" },
  checkPending: { FR: "Pas encore", EN: "Not yet" },
  checkRefused: { FR: "Non", EN: "No" },
  // A segment is too narrow for a sentence, so the full meaning lives in the
  // tooltip. Every card control has one, since several of them are clipped or
  // icon-only by design.
  checkAcceptedHint: {
    FR: "L'invitation a été acceptée : passer à la suite",
    EN: "The invitation was accepted: move on",
  },
  checkPendingHint: {
    FR: "Toujours pas acceptée : redemander plus tard",
    EN: "Still not accepted: ask again later",
  },
  callContactHint: { FR: "Appeler ce contact", EN: "Call this contact" },
  openProfileHint: {
    FR: "Ouvrir le profil LinkedIn",
    EN: "Open the LinkedIn profile",
  },
  markDoneHint: {
    FR: "Marquer cette étape comme faite",
    EN: "Mark this step as done",
  },
  disqualifyHint: {
    FR: "Écarter ce contact : il ne sera jamais démarché",
    EN: "Rule this contact out: it will never be prospected",
  },
  checkAskedAgain: {
    FR: "Redemandé dans {n} j ({count}/{max})",
    EN: "Asked again in {n} d ({count}/{max})",
  },

  // ── terminal ──
  outcomeFinished: { FR: "Séquence terminée", EN: "Sequence finished" },
  outcomeReplied: { FR: "A répondu", EN: "Replied" },
  outcomeInterested: { FR: "Intéressé", EN: "Interested" },
  outcomeUnsubscribed: { FR: "Désinscrit", EN: "Unsubscribed" },
  outcomeBounced: { FR: "Adresse invalide", EN: "Bounced" },
  outcomeStopped: { FR: "Arrêté", EN: "Stopped" },

  // ── stats ──
  statsContactsPerDay: {
    FR: "Contacts entrés / jour",
    EN: "Contacts entering / day",
  },
  statsContactsPerDayHint: {
    FR: "moyenne sur 30 j",
    EN: "30-day average",
  },
  // "Executed" and not "done": a step closed because it did not apply (no
  // LinkedIn profile) is not something anybody did.
  statsStepsExecuted: {
    FR: "Étapes réalisées ({n} j)",
    EN: "Steps carried out ({n} d)",
  },
  statsStepsExecutedHint: {
    FR: "dont {n} par l'équipe",
    EN: "{n} of them by the team",
  },
  statsStepsExecutedAuto: {
    FR: "aucune réalisée par l'équipe",
    EN: "none carried out by the team",
  },
  statsAvgProgress: { FR: "Avancement moyen", EN: "Average progress" },
  statsStepsRatio: {
    FR: "{executed} / {applicable} étapes applicables",
    EN: "{executed} / {applicable} applicable steps",
  },
  // Median, not mean: one contact parked a fortnight on a step used to drag the
  // headline away from what every other contact actually experiences.
  statsMedianStepTime: {
    FR: "Temps médian par étape",
    EN: "Median time per step",
  },
  statsNoTimingYet: {
    FR: "Aucune étape terminée pour le moment : le temps par étape se mesure dès la première.",
    EN: "No step completed yet: time per step is measured from the first one.",
  },
  // ── step table ──
  statsWhereStuck: { FR: "Où sont les contacts", EN: "Where contacts are" },
  statsColStep: { FR: "Étape", EN: "Step" },
  statsColDone: { FR: "Réalisées", EN: "Carried out" },
  statsColTime: { FR: "Temps médian", EN: "Median time" },
  statsColSkipped: { FR: "Ignorées", EN: "Skipped" },
  statsBottleneck: { FR: "Goulot", EN: "Bottleneck" },
  statsStepTiming: {
    FR: "Moyenne {avg} · {n} étapes chronométrées",
    EN: "Mean {avg} · {n} timed steps",
  },
  statsNoWaiting: {
    FR: "Aucun contact en attente : la séquence est terminée pour tout le monde.",
    EN: "No contact waiting: the sequence is over for everybody.",
  },
  statsNeverReached: { FR: "Jamais atteinte", EN: "Never reached" },
  // ── exits ──
  statsExitTotal: {
    FR: "{n} sorties sur {days} j",
    EN: "{n} exits over {days} d",
  },
  statsThroughput: { FR: "Débit quotidien", EN: "Daily throughput" },
  statsStarted: { FR: "Contacts entrés", EN: "Contacts started" },
  statsFinished: { FR: "Contacts terminés", EN: "Contacts finished" },
  // ── actions per day ──
  // Human work only, on working days: what the team itself got through.
  statsActions: {
    FR: "Actions de l'équipe par jour ouvré",
    EN: "Team actions per working day",
  },
  statsActionsWindow: {
    FR: "{days} derniers jours, hors week-ends",
    EN: "last {days} days, weekends left out",
  },
  statsCalls: { FR: "Appels", EN: "Calls" },
  statsEmails: { FR: "Emails", EN: "Emails" },
  statsLinkedin: { FR: "Actions LinkedIn", EN: "LinkedIn actions" },
  statsCallsPerDay: { FR: "Appels / jour", EN: "Calls / day" },
  statsEmailsPerDay: { FR: "Emails / jour", EN: "Emails / day" },
  statsLinkedinPerDay: {
    FR: "Actions LinkedIn / jour",
    EN: "LinkedIn actions / day",
  },
  statsPerDaySince: {
    FR: "{total} sur {days} j ouvrés, depuis le {first}",
    EN: "{total} over {days} working days, since {first}",
  },
  statsPerDayNone: {
    FR: "Aucune action de l'équipe pour l'instant",
    EN: "No team action yet",
  },
  statsNoActionYet: {
    FR: "Aucun appel, email ni action LinkedIn réalisé par l'équipe sur cette période.",
    EN: "No call, email or LinkedIn action carried out by the team over this period.",
  },
  statsEmpty: {
    FR: "Pas encore assez d'historique pour des statistiques.",
    EN: "Not enough history for statistics yet.",
  },

  // ── history sheet ──
  historyTitle: { FR: "Historique du contact", EN: "Contact history" },
  historyEmpty: { FR: "Aucune étape terminée.", EN: "No step closed yet." },
  historyBySystem: { FR: "Automatique", EN: "Automatic" },
  leadHistoryTitle: { FR: "Étapes réalisées", EN: "Completed steps" },

  // ── conversation (emails mirrored from the sending tool) ──
  messageInbound: { FR: "Réponse du prospect", EN: "Prospect reply" },
  conversationEmpty: {
    FR: "Aucun email échangé pour le moment.",
    EN: "No email exchanged yet.",
  },
  conversationShow: { FR: "Voir la conversation", EN: "Show conversation" },
  replyPlaceholder: {
    FR: "Répondre au prospect...",
    EN: "Answer the prospect...",
  },
  replySend: { FR: "Envoyer", EN: "Send" },
  replySent: { FR: "Réponse envoyée.", EN: "Answer sent." },
  conversationHide: { FR: "Masquer la conversation", EN: "Hide conversation" },
  hotUnread: { FR: "{n} {n|non lu|non lus}", EN: "{n} unread" },
  messageOutbound: { FR: "Email envoyé", EN: "Email sent" },
  conversationYou: { FR: "Vous", EN: "You" },
  conversationStep: { FR: "Email {n}", EN: "Email {n}" },
  conversationUnread: { FR: "Non lu", EN: "Unread" },
  conversationOrder: {
    FR: "{n} messages, du plus ancien au plus récent",
    EN: "{n} messages, oldest first",
  },
  messageShowMore: { FR: "Voir tout", EN: "Show more" },
  messageShowLess: { FR: "Réduire", EN: "Show less" },
  unreadReplies: { FR: "Réponses non lues", EN: "Unread replies" },

  // ── step status (the ladder, and the history rows) ──
  stepDone: { FR: "Fait", EN: "Done" },
  stepPending: { FR: "En cours", EN: "In progress" },
  // Not "blocked": nothing is wrong with the step, its turn simply has not come.
  stepBlocked: { FR: "À venir", EN: "Upcoming" },
  stepSkipped: { FR: "Ignoré", EN: "Skipped" },
  stepCancelled: { FR: "Annulé", EN: "Cancelled" },
  skipNoLinkedin: { FR: "pas de profil LinkedIn", EN: "no LinkedIn profile" },
  skipNoPhone: { FR: "pas de téléphone", EN: "no phone number" },
  skipCallFirstContact: {
    FR: "appels déjà passés au premier contact",
    EN: "calls already made on the first contact",
  },
  phoneRoster: { FR: "Numéros à appeler", EN: "Numbers to call" },
  skipNoEmail: { FR: "pas d'adresse e-mail", EN: "no email address" },
  skipInviteRefused: { FR: "invitation refusée", EN: "invitation declined" },
  skipInviteNotAccepted: {
    FR: "invitation jamais acceptée",
    EN: "invitation never accepted",
  },
  skipManual: { FR: "passé manuellement", EN: "skipped by hand" },
  skipTrackStopped: { FR: "séquence arrêtée", EN: "sequence stopped" },
  skipPushFailed: { FR: "envoi impossible", EN: "send failed" },
  skipAbandoned: { FR: "abandonné", EN: "abandoned" },
  skipBackfillHistorical: { FR: "historique repris", EN: "backfilled history" },
  skipLeadTransformed: { FR: "lead transformé", EN: "lead transformed" },
  awaitingSend: { FR: "En attente d'envoi", EN: "Waiting to be sent" },
  awaitingDelivery: { FR: "Lead non livré", EN: "Lead not delivered" },
  awaitingDeliveryHint: {
    FR: "Ce lead n'a pas encore été livré : aucune synchronisation ne peut l'envoyer avant sa livraison. Les étapes manuelles restent réalisables.",
    EN: "This lead has not been delivered yet: no sync can send it before delivery. Its manual steps remain workable.",
  },
  pushFailed: { FR: "Envoi échoué", EN: "Send failed" },
  retrySend: { FR: "Réessayer l'envoi", EN: "Retry send" },
  retrySendStaged: {
    FR: "Contact transmis à l'outil d'envoi.",
    EN: "Contact handed to the sending tool.",
  },
  retrySendDispatched: {
    FR: "Nouvel envoi lancé.",
    EN: "Send re-dispatched.",
  },
  retrySendAlreadySent: {
    FR: "Ce contact est déjà chez l'outil d'envoi.",
    EN: "The sending tool already holds this contact.",
  },
  retrySendDrafting: {
    FR: "Le message est encore en cours de rédaction.",
    EN: "The copy is still being written.",
  },
  retrySendUndelivered: {
    FR: "Impossible : le lead doit d'abord être livré.",
    EN: "Not possible: the lead must be delivered first.",
  },
  retrySendEnrichment: {
    FR: "Impossible : l'enrichissement du lead est encore en cours.",
    EN: "Not possible: the lead is still being enriched.",
  },
  retrySendExhausted: {
    FR: "Plus aucun contact à envoyer sur ce lead.",
    EN: "No contact left to send on this lead.",
  },
  retrySendRefused: {
    FR: "Envoi impossible pour le moment.",
    EN: "The send cannot happen right now.",
  },
  awaitingCompletion: {
    FR: "Envoi après complétion",
    EN: "Sends after completion",
  },
  awaitingCompletionHint: {
    FR: "Le lead est encore en complétion. Le contact part vers l'outil dès la fin du tick, sans attendre la prochaine livraison.",
    EN: "The lead is still being completed. The contact leaves for the tool as soon as the tick ends, without waiting for the next delivery.",
  },

  // ── contact dialog ──
  contactDialogTitle: { FR: "Fiche contact", EN: "Contact sheet" },
  contactDialogDesc: {
    FR: "Le contact, son avancement, les notes de l'équipe et les autres contacts de l'entreprise.",
    EN: "The contact, its progress, the team's notes and the company's other contacts.",
  },
  sectionLadder: { FR: "Étapes du contact", EN: "Contact steps" },
  sectionNotes: { FR: "Notes", EN: "Notes" },
  sectionConversation: { FR: "Conversation", EN: "Conversation" },
  sectionContacts: { FR: "Autres contacts", EN: "Other contacts" },
  // The contact the sequence is actually running on, as opposed to the one the
  // dialog happens to be open on.
  siblingActive: {
    FR: "Contact en cours dans le pipeline",
    EN: "Contact currently in the pipeline",
  },
  sectionCompany: { FR: "Fiche entreprise", EN: "Company card" },
  notePlaceholder: {
    FR: "Ce que vous avez appris, ce qu'il faut savoir avant de rappeler...",
    EN: "What you learned, what the next call should know...",
  },
  noteAdd: { FR: "Ajouter la note", EN: "Add note" },
  noteAdded: { FR: "Note ajoutée.", EN: "Note added." },
  noteDelete: { FR: "Supprimer la note", EN: "Delete note" },
  noteOnCompany: { FR: "Sur l'entreprise", EN: "On the company" },

  // -- tasks: a note with a deadline --
  sectionTasks: { FR: "Tâches", EN: "Tasks" },
  sectionNotesOnly: { FR: "Notes", EN: "Notes" },
  taskDeadline: { FR: "Échéance", EN: "Deadline" },
  taskDeadlinePlaceholder: {
    FR: "Demain, lundi prochain...",
    EN: "Tomorrow, next monday...",
  },
  taskDeadlineClear: { FR: "Retirer l'échéance", EN: "Clear the deadline" },
  taskAdd: { FR: "Ajouter la tâche", EN: "Add task" },
  taskOverdue: { FR: "En retard", EN: "Overdue" },
  taskOverdueDays: { FR: "{days} j de retard", EN: "{days}d late" },
  taskDueSoon: { FR: "Bientôt", EN: "Due soon" },
  taskDone: { FR: "Marquer comme faite", EN: "Mark as done" },
  taskReopen: { FR: "Rouvrir la tâche", EN: "Reopen task" },
  taskOnCompany: { FR: "Tâche sur l'entreprise", EN: "Task on the company" },

  // -- agenda table --
  viewBoard: { FR: "Tableau", EN: "Board" },
  viewList: { FR: "Liste", EN: "List" },
  viewListHint: {
    FR: "{n} à faire, dont {late} en retard",
    EN: "{n} to do, {late} of them late",
  },
  viewListHintOnTime: { FR: "{n} à faire", EN: "{n} to do" },
  agendaEmpty: {
    FR: "Rien à faire avec ces filtres.",
    EN: "Nothing to do with these filters.",
  },
  agendaColumnDue: { FR: "Échéance", EN: "Due" },
  agendaColumnSource: { FR: "Origine", EN: "Source" },
  agendaColumnCompany: { FR: "Entreprise", EN: "Company" },
  agendaColumnContact: { FR: "Contact", EN: "Contact" },
  agendaColumnWhat: { FR: "À faire", EN: "To do" },
  agendaColumnState: { FR: "État", EN: "State" },
  agendaSourceLabel: { FR: "Origine des tâches", EN: "Task source" },
  agendaFilterLabel: { FR: "État des tâches", EN: "Task state" },
  agendaSourceManual: { FR: "Manuelle", EN: "Manual" },
  agendaSourceAuto: { FR: "Séquence", EN: "Sequence" },
  agendaSourceAll: { FR: "Toutes", EN: "All" },
  agendaFilterOpen: { FR: "En cours", EN: "Open" },
  agendaFilterDone: { FR: "Faites", EN: "Done" },
  agendaFilterAll: { FR: "Toutes", EN: "All" },
  agendaNoDeadline: { FR: "Sans échéance", EN: "No deadline" },
  contactFinished: { FR: "Terminée", EN: "Finished" },
  contactWorking: { FR: "En cours", EN: "In progress" },
  contactQueued: { FR: "En attente", EN: "Waiting" },
  contactCurrent: { FR: "Contact en cours", EN: "Current contact" },
  enrollOn: { FR: "Enrôlement le {date}", EN: "Enrolled on {date}" },
  cooldownHint: {
    FR: "Le contact suivant est enrôlé {n} j après la fin du précédent.",
    EN: "The next contact is enrolled {n} d after the previous one ends.",
  },

  // ── transformation / hot prospects ──
  cancel: { FR: "Annuler", EN: "Cancel" },
  // "Chaud" rather than "Gagné": the lead is not closed, it is warm enough to
  // stop prospecting and hand over to the rep, which is what the list it joins
  // is called.
  transform: { FR: "Chaud", EN: "Hot" },
  // The button that makes it so: a verb, so the header never reads the action
  // as the lead's current status.
  transformAction: { FR: "Passer en chaud", EN: "Mark as hot" },
  transformHint: {
    FR: "Classe le lead comme chaud : il sort du pipeline pour les prospects chauds et ses autres contacts ne sont plus démarchés.",
    EN: "Marks the lead as hot: it leaves the pipeline for the hot prospects and its other contacts stop being prospected.",
  },
  transformConfirm: {
    FR: "Marquer ce lead comme chaud ? Il quitte le pipeline pour les prospects chauds et ses autres contacts ne seront plus démarchés.",
    EN: "Mark this lead as hot? It leaves the pipeline for the hot prospects and its other contacts will no longer be prospected.",
  },
  transformed: {
    FR: "Lead chaud : il rejoint les prospects chauds.",
    EN: "Lead marked hot: it moves to the hot prospects.",
  },
  // ── the verdict dialog ──
  disqualifyDesc: {
    FR: "Que faut-il faire de {name} chez {company} ?",
    EN: "What should happen to {name} at {company}?",
  },
  disqualifyScope: { FR: "Portée", EN: "Scope" },
  disqualifyScopeContact: { FR: "Ce contact", EN: "This contact" },
  disqualifyScopeLead: { FR: "Toute l'entreprise", EN: "The whole company" },
  disqualifyScopeContactOnly: {
    FR: "Seul ce contact est écarté : ses collègues gardent leur tour.",
    EN: "Only this contact is ruled out: its colleagues keep their turn.",
  },
  disqualifyScopeLeadOnly: {
    FR: "Ce contact a déjà quitté la séquence : le verdict porte sur toute l'entreprise.",
    EN: "This contact has already left the sequence: the verdict covers the whole company.",
  },
  disqualifyReason: { FR: "Motif", EN: "Reason" },
  disqualifyNote: { FR: "Précisez", EN: "Tell us more" },
  disqualifyNotePlaceholder: {
    FR: "En une ligne, pourquoi ?",
    EN: "In one line, why?",
  },
  disqualifiedContact: {
    FR: "Contact écarté : au suivant de l'entreprise.",
    EN: "Contact ruled out: on to the company's next one.",
  },
  disqualifiedLead: {
    FR: "Entreprise écartée : personne d'autre n'y sera démarché.",
    EN: "Company ruled out: nobody else there will be prospected.",
  },
  disqualifyNoteRequired: {
    FR: "Obligatoire pour ce motif.",
    EN: "Required for this reason.",
  },
  disqualifyNoteOptional: {
    FR: "Facultatif, mais vos collègues le liront.",
    EN: "Optional, but your colleagues will read it.",
  },
  snoozeNotePlaceholder: {
    FR: "Qu'avez-vous appris ? À savoir avant de rappeler.",
    EN: "What did you learn? Worth knowing before calling back.",
  },
  snoozeDate: { FR: "Rappeler le", EN: "Call back on" },
  snoozeDateHint: {
    FR: "L'entreprise sort du pipeline : vous la retrouverez dans vos tâches à cette date.",
    EN: "The company leaves the pipeline: you get it back in your tasks on that date.",
  },
  snoozeChannel: { FR: "Comment reprendre", EN: "How to resume" },
  snoozeChannelHint: {
    FR: "Indication portée par la tâche, aucune étape n'est modifiée.",
    EN: "An instruction carried by the task; no step is changed.",
  },
  snoozeConfirm: { FR: "Programmer le rappel", EN: "Schedule the call back" },
  snoozedUntil: {
    FR: "Entreprise à rappeler le {date} : la tâche vous attend.",
    EN: "Company to call back on {date}: the task is waiting for you.",
  },
  taskFromPostponement: { FR: "Issue d'un report", EN: "From a postponement" },
  statsExitReasons: { FR: "Motifs de sortie", EN: "Why contacts left" },
  statsNoExitYet: {
    FR: "Aucun contact sorti du pipeline sur la période.",
    EN: "No contact has left the pipeline over the period.",
  },
  tabBoard: { FR: "Prospects", EN: "Prospects" },
  expandColumn: { FR: "Déplier la colonne", EN: "Expand column" },
  collapseColumn: { FR: "Replier la colonne", EN: "Collapse column" },
  tabHot: { FR: "Chauds, clients et perdus", EN: "Hot, clients and lost" },
  hotEmpty: {
    FR: "Aucun prospect chaud pour le moment.",
    EN: "No hot prospect yet.",
  },
  hotEmptyHint: {
    FR: "Un lead arrive ici dès qu'un contact répond ou qu'un commercial le marque transformé.",
    EN: "A lead lands here as soon as a contact replies or a rep marks it transformed.",
  },
  hotOn: { FR: "Transformé le {date}", EN: "Transformed on {date}" },
  hotByProvider: { FR: "Réponse détectée", EN: "Reply detected" },
  hotByUser: { FR: "Déclaré par {name}", EN: "Declared by {name}" },
  hotContactsWorked: {
    FR: "{n} {n|contact démarché|contacts démarchés}",
    EN: "{n} {n|contact|contacts} prospected",
  },
  hotStatsCount: { FR: "Prospects chauds", EN: "Hot prospects" },
  hotStatsCountHint: {
    FR: "{n} sur les {days} derniers jours",
    EN: "{n} over the last {days} days",
  },
  hotStatsTransformRate: { FR: "Taux de transformation", EN: "Transform rate" },
  hotStatsTransformRateHint: {
    FR: "sur {n} {n|lead|leads} en prospection",
    EN: "of {n} {n|lead|leads} prospected",
  },
  hotStatsLost: { FR: "Perdus après transformation", EN: "Lost after winning" },
  hotStatsLostHint: {
    FR: "{n} % des transformations encore ouvertes",
    EN: "{n} % of wins still open",
  },
  hotStatsAvgContacts: {
    FR: "Contacts par transformation",
    EN: "Contacts per win",
  },
  hotStatsAvgContactsHint: {
    FR: "moyenne, pertes incluses",
    EN: "average, losses included",
  },
  hotLostNoteRequired: {
    FR: "Obligatoire avec « Autre ».",
    EN: 'Required with "Other".',
  },
  hotLostNoteOptional: { FR: "Facultatif.", EN: "Optional." },
  lostBannerTitle: { FR: "Transformation perdue :", EN: "Win lost:" },
  lostBannerOn: { FR: "Le {date}", EN: "On {date}" },
  lostBannerBy: { FR: "par {name}", EN: "by {name}" },
  hotClientOn: { FR: "Client depuis le {date}", EN: "Client since {date}" },
  hotClientsEmpty: {
    FR: "Aucun client signé pour le moment.",
    EN: "No signed client yet.",
  },
  hotClientsEmptyHint: {
    FR: "Marquez un prospect chaud comme client dès qu'il signe.",
    EN: "Mark a hot prospect as a client as soon as it signs.",
  },
  hotStatsClients: { FR: "Clients", EN: "Clients" },
  hotStatsClientsHint: {
    FR: "prospects chauds signés",
    EN: "hot prospects that signed",
  },
  hotLostEmpty: {
    FR: "Aucune transformation perdue.",
    EN: "No win fell through yet.",
  },

  // ── units ──
  unitSecond: { FR: "s", EN: "s" },
  unitMinute: { FR: "min", EN: "min" },
  unitHour: { FR: "h", EN: "h" },
  unitDay: { FR: "j", EN: "d" },

  // ── Instagram and WhatsApp steps ──
  openInstagram: { FR: "Instagram", EN: "Instagram" },
  openInstagramHint: {
    FR: "Ouvrir le compte Instagram",
    EN: "Open the Instagram account",
  },
  openWhatsapp: { FR: "WhatsApp", EN: "WhatsApp" },
  openWhatsappHint: {
    FR: "Ouvrir la conversation WhatsApp",
    EN: "Open the WhatsApp chat",
  },
  skipChannelRefused: {
    FR: "le canal a refusé le destinataire",
    EN: "the channel refused the recipient",
  },
  skipSuppressed: {
    FR: "contact désinscrit",
    EN: "contact opted out",
  },
  skipEmailBounced: {
    FR: "adresse email invalide",
    EN: "email address bounced",
  },
  skipNoInstagram: {
    FR: "pas de compte Instagram",
    EN: "no Instagram account",
  },
  skipNoWhatsapp: { FR: "pas de mobile WhatsApp", EN: "no WhatsApp mobile" },

  // ── connected messaging accounts ──
  sentFromAccount: {
    FR: "Envoyé automatiquement depuis {account}",
    EN: "Sent automatically from {account}",
  },
  auditAttached: { FR: "Audit PDF joint", EN: "Audit PDF attached" },
  generatingAudit: {
    FR: "Audit PDF en cours de génération",
    EN: "Generating the audit PDF",
  },
  sendingFromAccount: {
    FR: "Envoi automatique depuis {account}",
    EN: "Sending automatically from {account}",
  },
  replyOnChannel: { FR: "Réponse via {channel}", EN: "Reply on {channel}" },
  replyChannelPick: { FR: "Répondre via", EN: "Reply on" },
} satisfies FullTranslation
